import { Injectable } from '@angular/core';

import { filter, map } from 'rxjs';

import {
  CamBaseService,
  GraphEndpoint,
  GraphReponsePaged,
  HandleComplexRequest,
  graphAggregatorEndpoint,
  graphQlUpdateFields,
} from '@camelot/server';
import { userProps } from '@camelot/user';
import { isNonNullable } from '@camelot/utils';

import { colorSetProps } from './dto/color-set';
import { CalendarNote, calendarNoteProps } from './dto/note';
import { noteTypeProps } from './dto/note-type';
import { CalendarNoteCreationPayloadInput } from './dto/post/calendar-note-creation-payload-input';
import { CalendarNoteModifierPayloadInput } from './dto/post/calendar-note-modifier-payload-input';
import { CREATE_CALENDAR_NOTE, UPDATE_CALENDAR_NOTE } from './mutations';
import { GET_CALENDAR_NOTES } from './queries';

const graphEndpoint: GraphEndpoint = {
  clientName: 'planningService',
  endpoint: 'planning',
};

@Injectable({
  providedIn: 'root',
})
export class CamNotesService extends CamBaseService {
  public notes = new HandleComplexRequest<CalendarNote[]>();
  public note = new HandleComplexRequest<CalendarNote>();

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });
  }

  public fetchNotes$(userId: string, startDate: Date, endDate: Date) {
    return this.notes.fetch(
      userId,
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<CalendarNote>>(
          GET_CALENDAR_NOTES({
            where: { userId: { eq: userId }, start: { gte: startDate.toISOString(), lte: endDate.toISOString() } },
            props: `items {
                ${calendarNoteProps.get('id')}
                ${calendarNoteProps.get('start')}
                ${calendarNoteProps.get('end')}
                ${calendarNoteProps.get('title')}
                ${calendarNoteProps.get('noteType')} {
                  ${noteTypeProps.get('colorSet')} {
                    ${colorSetProps.get('backgroundColor')}
                    ${colorSetProps.get('borderColor')}
                    ${colorSetProps.get('textColor')}
                  }
                }
              }`,
          }),
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public fetchNotesForUsers$(usersId: string[], startDate: Date, endDate: Date) {
    return this.notes.fetch(
      usersId.join('-'),
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<CalendarNote>>(
          GET_CALENDAR_NOTES({
            where: { userId: { in: usersId }, start: { gte: startDate.toISOString(), lte: endDate.toISOString() } },
            props: `items {
                ${calendarNoteProps.get('id')}
                ${calendarNoteProps.get('start')}
                ${calendarNoteProps.get('end')}
                ${calendarNoteProps.get('title')}
                ${calendarNoteProps.get('userId')}
                ${calendarNoteProps.get('noteType')} {
                  ${noteTypeProps.get('colorSet')} {
                    ${colorSetProps.get('backgroundColor')}
                    ${colorSetProps.get('borderColor')}
                    ${colorSetProps.get('textColor')}
                  }
                }
              }`,
          }),
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public fetchById$(id: string) {
    return this.note.fetch(
      id,
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<CalendarNote>>(
          GET_CALENDAR_NOTES({
            where: { id: { eq: id } },
            props: `items {
                ${calendarNoteProps.get('id')}
                ${calendarNoteProps.get('start')}
                ${calendarNoteProps.get('end')}
                ${calendarNoteProps.get('title')}
                ${calendarNoteProps.get('noteType')} {
                  ${noteTypeProps.get('denomination')}
                  ${noteTypeProps.get('id')}
                  ${noteTypeProps.get('colorSet')} {
                    ${colorSetProps.get('backgroundColor')}
                    ${colorSetProps.get('borderColor')}
                    ${colorSetProps.get('textColor')}
                  }
                }
                ${calendarNoteProps.get('userId')}
                ${calendarNoteProps.get('user')} {
                  ${userProps.get('id')}
                  ${userProps.get('firstName')}
                  ${userProps.get('lastName')}
                }
              }`,
          }),
          graphAggregatorEndpoint.clientName
        )
        .pipe(
          map(data => (data.items && data.items.length === 1 ? data.items[0] : null)),
          filter(isNonNullable)
        )
    );
  }

  public createCalendarNote$(calendarNote: CalendarNoteCreationPayloadInput) {
    return this._graphService
      .mutateBuilder<CalendarNote>(CREATE_CALENDAR_NOTE(calendarNote), graphEndpoint.clientName, ['unavailabilities'])
      .pipe(filter(isNonNullable));
  }

  public update$(calendarNote: Partial<CalendarNoteModifierPayloadInput>) {
    return this._graphService.mutateBuilder<CalendarNote>(
      UPDATE_CALENDAR_NOTE({ ...calendarNote, ...graphQlUpdateFields(calendarNote) }),
      graphEndpoint.clientName,
      ['unavailabilities']
    );
  }
}
